/**
 * The sheets People and Home open on people: a note to one person, one note to a whole group, and putting people
 * in an automation. A note to one person goes into their thread as an email; a group's note goes to each into
 * their own thread, through the queue.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { type NoteAbout, startingNote } from "../../../shared/people-notes";
import type { AutomationSummary, HomePerson } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Button } from "../../components/ui/Button";
import { Face, FaceStack } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Sheet } from "../../components/ui/Sheet";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";
import { firstName, fmt } from "../../lib/when";

const SETUP_EMAIL = "/setup/email";

/** Words arrive a few letters at a time, the way AI drafts land in the prototype. */
function useTypeInto(): [string, (v: string) => void, (text: string) => void] {
  const [value, setValue] = useState("");
  const timer = useRef(0);
  useEffect(() => () => window.clearInterval(timer.current), []);
  const typeInto = (text: string) => {
    window.clearInterval(timer.current);
    let i = 0;
    setValue("");
    timer.current = window.setInterval(() => {
      i = Math.min(text.length, i + 3);
      setValue(text.slice(0, i));
      if (i >= text.length) window.clearInterval(timer.current);
    }, 12);
  };
  const set = (v: string) => {
    window.clearInterval(timer.current);
    setValue(v);
  };
  return [value, set, typeInto];
}

/** An email that could not go because sending is off says so, with the way to set it up. */
function useSendTrouble(): (err: unknown) => void {
  const toast = useToast();
  const navigate = useNavigate();
  return (err) => {
    if (err instanceof ApiError && err.status === 409) {
      toast({ text: "Your emails are off. Set up your email first.", icon: "mail", action: { label: "Set up email", onClick: () => navigate(SETUP_EMAIL) } });
    } else {
      toast({ text: err instanceof ApiError ? err.message : "That didn't go. Try again.", icon: "info" });
    }
  };
}

interface NotePerson extends HomePerson {
  email?: string;
}

/** "Send Grace a note": one email to one person, into their thread. */
export function NoteSheet({
  person,
  about = "any",
  sub,
  pill,
  onClose,
  onSent,
}: {
  /** Who it is for; null keeps the sheet shut. */
  person: NotePerson | null;
  about?: NoteAbout;
  sub?: string;
  pill?: React.ReactNode;
  onClose: () => void;
  onSent?: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const trouble = useSendTrouble();
  const ids = useId();
  const [subject, setSubject] = useState("");
  const [body, setBody, typeInto] = useTypeInto();
  const [busy, setBusy] = useState<"" | "ai" | "send">("");

  useEffect(() => {
    if (!person) return;
    const start = startingNote(about, user?.first_name ?? "");
    setSubject(start.subject);
    setBody(start.text.replace("{{first name}}", firstName(person.name)));
    // Why only the person: a new person is a new note; the rest is read once when it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.id]);

  if (!person) return null;
  const first = firstName(person.name);

  const draft = async () => {
    setBusy("ai");
    try {
      const { contactId } = await api.talk.directFor(person.id);
      typeInto((await api.talk.draftDirect(contactId)).draft);
    } catch {
      toast({ text: "AI could not write one just now. Try again.", icon: "info" });
    } finally {
      setBusy("");
    }
  };
  const send = async () => {
    if (!body.trim()) return;
    setBusy("send");
    try {
      const { contactId } = await api.talk.directFor(person.id);
      await api.talk.sendDirect(contactId, { channel: "email", subject: subject.trim(), body: body.trim(), clientId: crypto.randomUUID() });
      toast({ text: `Sent to ${first}`, icon: "send" });
      onSent?.();
      onClose();
    } catch (err) {
      trouble(err);
    } finally {
      setBusy("");
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={`Send ${first} a note`}
      sub={sub}
      wide
      footer={
        <>
          <button type="button" className="aibtn" style={{ marginRight: "auto" }} onClick={draft} disabled={busy !== ""}>
            <Icon name="sparkle" /> Draft with AI
          </button>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="send" onClick={send} disabled={busy !== "" || !body.trim()}>
            Send
          </Button>
        </>
      }
    >
      <div className="col" style={{ gap: 14 }}>
        <div className="row g12">
          <Face name={person.name} photo={person.avatar_url} size={40} />
          <div style={{ minWidth: 0 }}>
            <div className="b7"><bdi>{person.name}</bdi></div>
            {person.email ? <div className="small muted">{person.email}</div> : null}
          </div>
          <span className="sp" />
          {pill}
        </div>
        <div className="field">
          <label htmlFor={`${ids}-s`}>Subject</label>
          <input dir="auto" id={`${ids}-s`} className="inp" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`${ids}-b`}>Message</label>
          <textarea dir="auto" id={`${ids}-b`} className="inp" rows={7} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
      </div>
    </Sheet>
  );
}

/** "Send a note to all 58": one note, to each of them, into their own thread. */
export function GroupNoteSheet({
  open,
  title,
  label,
  about,
  people,
  onClose,
}: {
  open: boolean;
  /** The button's words: "Send the replay to the 12". */
  title: string;
  /** The group, as People names it. */
  label: string;
  about: NoteAbout;
  people: ReadonlyArray<{ id: string; name: string; avatar_url: string | null }>;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const trouble = useSendTrouble();
  const ids = useId();
  const [subject, setSubject] = useState("");
  const [body, setBody, typeInto] = useTypeInto();
  const [busy, setBusy] = useState<"" | "ai" | "send">("");

  useEffect(() => {
    if (!open) return;
    const start = startingNote(about, user?.first_name ?? "");
    setSubject(start.subject);
    setBody(start.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, about]);

  const n = people.length;
  const draft = async () => {
    setBusy("ai");
    try {
      const d = await api.people.emailDraft({ group: label, stage: about });
      setSubject(d.subject);
      typeInto(d.text);
    } catch {
      toast({ text: "AI could not write one just now. Try again.", icon: "info" });
    } finally {
      setBusy("");
    }
  };
  const send = async () => {
    setBusy("send");
    try {
      const { queued } = await api.people.email({ registrant_ids: people.map((p) => p.id), subject: subject.trim(), text: body.trim() });
      toast({ text: `Sending to ${fmt(queued)} ${queued === 1 ? "person" : "people"}. Replies land in Conversations.`, icon: "send" });
      onClose();
    } catch (err) {
      trouble(err);
    } finally {
      setBusy("");
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      wide
      footer={
        <>
          <button type="button" className="aibtn" style={{ marginRight: "auto" }} onClick={draft} disabled={busy !== ""}>
            <Icon name="sparkle" /> Draft with AI
          </button>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="send" onClick={send} disabled={busy !== "" || !body.trim() || !subject.trim() || n === 0}>
            {`Send to ${fmt(n)}`}
          </Button>
        </>
      }
    >
      <div className="col" style={{ gap: 14 }}>
        <div className="row g12">
          <FaceStack people={people.slice(0, 5).map((p) => ({ name: p.name, photo: p.avatar_url }))} size={32} more={Math.max(0, n - 5)} />
          <div>
            <div className="b7">{`${fmt(n)} ${n === 1 ? "person" : "people"}`}</div>
            <div className="small muted">{label}</div>
          </div>
        </div>
        <div className="field">
          <label htmlFor={`${ids}-s`}>Subject</label>
          <input dir="auto" id={`${ids}-s`} className="inp" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`${ids}-b`}>Message</label>
          <textarea dir="auto" id={`${ids}-b`} className="inp" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
      </div>
    </Sheet>
  );
}

/** "Add to an automation": the automations that are not drafts, one picked, and the people put in it. */
export function AutomationSheet({
  open,
  ids,
  onClose,
  onAdded,
}: {
  open: boolean;
  /** The registrations to put in it. */
  ids: readonly string[];
  onClose: () => void;
  onAdded?: () => void;
}) {
  const toast = useToast();
  const [autos, setAutos] = useState<AutomationSummary[] | null>(null);
  const [pick, setPick] = useState<string>("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    api.automations
      .list()
      .then((r) => {
        if (!alive) return;
        const usable = r.automations.filter((a) => a.status !== "draft");
        setAutos(usable);
        setPick((p) => p || usable[0]?.id || "");
      })
      .catch(() => alive && setAutos([]));
    return () => {
      alive = false;
    };
  }, [open]);

  const n = ids.length;
  const add = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const { started } = await api.people.enrollMany({ automation_id: pick, registrant_ids: [...ids] });
      toast({ text: started === n ? "Added" : started === 0 ? "They're in it already" : `Added ${fmt(started)}. The rest are in it already.`, icon: "zap" });
      onAdded?.();
      onClose();
    } catch (err) {
      toast({ text: err instanceof ApiError ? err.message : "That didn't work. Try again.", icon: "info" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Add to an automation"
      sub={`${fmt(n)} ${n === 1 ? "person" : "people"}`}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="zap" onClick={add} disabled={busy || !pick}>
            Add them
          </Button>
        </>
      }
    >
      <div className="col" style={{ gap: 8 }} role="radiogroup" aria-label="Automations">
        {autos === null ? <div className="small muted">Loading your automations</div> : null}
        {autos?.length === 0 ? <div className="small muted">No automations are on yet.</div> : null}
        {autos?.map((a) => (
          <button
            key={a.id}
            type="button"
            role="radio"
            aria-checked={pick === a.id}
            className={pick === a.id ? "radio on" : "radio"}
            onClick={() => setPick(a.id)}
            style={{ textAlign: "left", font: "inherit", color: "inherit" }}
          >
            <span className="rd" />
            <div>
              <div className="b7">{a.name}</div>
              <div className="small muted">{a.trigger_label}</div>
            </div>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

/** "Delete Priya?": the real confirm, with what goes too. */
export function DeleteSheet({ open, id, name, onClose }: { open: boolean; id: string; name: string; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.people.remove(id);
      toast({ text: `${name} is deleted`, icon: "trash" });
      navigate("/people", { replace: true });
    } catch (err) {
      toast({ text: err instanceof ApiError ? err.message : "That didn't work. Try again.", icon: "info" });
      setBusy(false);
    }
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Delete ${name}?`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="trash" onClick={remove} disabled={busy}>
            Delete
          </Button>
        </>
      }
    >
      <p className="ink2">Every registration, everything they did, and your notes on them go too.</p>
    </Sheet>
  );
}
